import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../../../store/index.ts'
import { setAuthenticatedPlanner, setGuests, setSeatingPlan, saveSession } from '../../../store/seatingSlice.ts'
import { registerPlannerLead, verifyPlannerPinEndpoint, fetchPlanner, fetchPlannerGuests, fetchSeatingPlan } from '../../../endpoint/index.ts'
import Navbar from '../../../components/organism/navbar/index.tsx'
import Footer from '../../../components/organism/footer/index.tsx'
import PlanerBtn from '../../../components/atom/planer-btn/index.tsx'
import DatePicker from '../../../components/molecule/datePicker/index.tsx'
import './index.css'

const WeddingPlanerRegister: React.FC = () => {
	const { t, i18n } = useTranslation('planner')
	const navigate = useNavigate()
	const dispatch = useAppDispatch()

	const [step, setStep] = useState<'form' | 'pin'>('form')
	const [email, setEmail] = useState('')
	const [weddingDate, setWeddingDate] = useState('')
	const [brideName, setBrideName] = useState('')
	const [groomName, setGroomName] = useState('')
	const [consent, setConsent] = useState(false)
	const [pin, setPin] = useState('')
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const formComplete = Boolean(
		email.trim() && weddingDate && brideName.trim() && groomName.trim() && consent
	)

	const handleRegister = async (e: React.FormEvent) => {
		e.preventDefault()
		// loading also guards the button, but this blocks a stray Enter-key
		// submit from firing a second registerPlannerLead call while the
		// first is still in flight.
		if (!formComplete || loading) return

		setLoading(true)
		setError(null)
		try {
			await registerPlannerLead({
				email: email.trim(),
				weddingDate,
				brideName: brideName.trim(),
				groomName: groomName.trim(),
				consent,
				language: i18n.language
			})
			setStep('pin')
		} catch (err) {
			setError(t('register.errors.generic'))
		} finally {
			setLoading(false)
		}
	}

	const handleVerify = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!pin.trim() || loading) return

		setLoading(true)
		setError(null)

		// Two phases, two different meanings for a failure. Up to the callable
		// the only plausible cause is the PIN the couple typed. Past it the PIN
		// has been proven right and the session exists, so anything that breaks
		// is ours — `plannerId` being set is what tells the two apart.
		let plannerId: string | null = null
		try {
			const verified = await verifyPlannerPinEndpoint(email.trim(), pin.trim())
			plannerId = verified.plannerId

			// Navigating alone used to be the whole of this handler, which is
			// why a first-time couple landed on a SECOND PIN form: the planner
			// page restores from '@seatingAuth' and renders its own PIN prompt
			// when it finds nothing. It also skips its own fetch when Redux
			// already holds the planner, so the guests and seating plan have to
			// be filled in here too, exactly as weddingPlanerLogin does, or the
			// couple arrives at an empty planner instead.
			const planner = await fetchPlanner(plannerId)
			if (!planner) throw new Error(`Planner ${plannerId} could not be read`)

			dispatch(setAuthenticatedPlanner(planner))

			const [guestsData, seatingPlanData] = await Promise.all([
				fetchPlannerGuests(plannerId),
				fetchSeatingPlan(plannerId)
			])
			dispatch(setGuests(guestsData))
			dispatch(setSeatingPlan(seatingPlanData))
			dispatch(saveSession())

			navigate(`/weddingPlanner/${plannerId}`)
		} catch (err) {
			console.error('Entering the planner failed:', err)
			// verifyPlannerPin folds "unknown email", "wrong PIN" and "orphaned
			// lead" into one identical error on purpose (Task 9) — the UI must
			// not pretend to know which one it was. But it must not blame the
			// PIN for a failure that happened after the PIN was accepted: that
			// would send the couple back to re-request an email they already
			// have, chasing a PIN that was never the problem.
			setError(plannerId ? t('register.errors.generic') : t('register.errors.invalidPin'))
		} finally {
			setLoading(false)
		}
	}

	// Registration is idempotent per email (Task 8): resubmitting the same
	// form resends the same PIN rather than minting a new one. If the first
	// email never arrives, going back to the form and pressing submit again
	// is the recovery path — so the fields stay filled in, only the PIN and
	// any stale error are cleared.
	const handleBackToForm = () => {
		setPin('')
		setError(null)
		setStep('form')
	}

	return (
		<div className='planer-register-page'>
			<Navbar scrollToContact={() => {}} />

			<div className='planer-register-content'>
				<div className='planer-register-card'>
					<h1>{t('register.title')}</h1>

					{step === 'form' && (
						<form className='planer-register-form' onSubmit={handleRegister}>
							<label htmlFor='email'>{t('register.emailLabel')}</label>
							<input id='email' type='email' value={email} disabled={loading}
								onChange={(e) => setEmail(e.target.value)} />

							<label htmlFor='weddingDate'>{t('register.weddingDateLabel')}</label>
							<DatePicker id='weddingDate' value={weddingDate} disabled={loading}
									locale={i18n.language} placeholder={t('register.weddingDatePlaceholder')}
									onChange={setWeddingDate} />

							<label htmlFor='brideName'>{t('register.brideNameLabel')}</label>
							<input id='brideName' type='text' value={brideName} disabled={loading}
								onChange={(e) => setBrideName(e.target.value)} />

							<label htmlFor='groomName'>{t('register.groomNameLabel')}</label>
							<input id='groomName' type='text' value={groomName} disabled={loading}
								onChange={(e) => setGroomName(e.target.value)} />

							<div className='planer-register-consent'>
								<input id='consent' type='checkbox' checked={consent} disabled={loading}
									onChange={(e) => setConsent(e.target.checked)} />
								<label htmlFor='consent'>
									{t('register.consentBefore')}{' '}
									<a href='/privacy-policy' target='_blank' rel='noreferrer'>
										{t('register.consentLinkText')}
									</a>
									{t('register.consentAfter')}
								</label>
							</div>

							{error && <p className='planer-register-error'>{error}</p>}

							<PlanerBtn
								title={loading ? t('register.submitting') : t('register.submit')}
								disabled={loading || !formComplete}
								type='submit'
								fullWidth
							/>
						</form>
					)}

					{step === 'pin' && (
						<form className='planer-register-form' onSubmit={handleVerify}>
							<p className='planer-register-subtitle'>
								{t('register.pinSentTo')} <strong>{email}</strong>
							</p>

							<label htmlFor='pin'>{t('register.pinLabel')}</label>
							<input id='pin' type='text' inputMode='numeric' value={pin} autoFocus
								disabled={loading} onChange={(e) => setPin(e.target.value)} />

							{error && <p className='planer-register-error'>{error}</p>}

							<PlanerBtn
								title={loading ? t('register.verifying') : t('register.verify')}
								disabled={loading || !pin.trim()}
								type='submit'
								fullWidth
							/>

							<p className='planer-register-resend-hint'>
								{t('register.resendHint')}{' '}
								<button type='button' className='planer-register-resend-link'
									disabled={loading} onClick={handleBackToForm}>
									{t('register.resendCta')}
								</button>
							</p>
						</form>
					)}
				</div>
			</div>

			<Footer />
		</div>
	)
}

export default WeddingPlanerRegister
